import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  clearSystemGenMessageReplacedFlag,
  replaceSystemGenMessage,
  setCurrentMessage,
  setFileUploadToMessage,
} from 'src/actions/chatActions';
import helpers from 'src/helpers';
import { STYLE_OPTIONS } from 'src/constants';

const isMessageEdit = true;

const filterOptionsDynamicFields = createFilterOptions({
  stringify: ({
    label, value
  }) => `${label} ${value}`
});

const filterOptionsTemplates = createFilterOptions({
  stringify: ({
    label, description
  }) => `${label} ${description}`
});

const isOptionEqualToValue = (option, value) => value?.value && value?.value === option?.value;

const renderOptionAutocomplete = (props, option) => (
  <div
    {...props}
    style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}
  >
    <b>{option.label}</b>
    {(option.value || option.description) && (
      <Box
        sx={{
          color: STYLE_OPTIONS.COLOR_BLUE,
          fontFamily: STYLE_OPTIONS.FONT_FAMILY,
          fontSize: '12px',
        }}
      >
        {option.description
          ? option.description.replace(/(\r\n|\n|\r)/gm, ', ')
          : option.value}
      </Box>
    )}
  </div>
);

function TemplateAndDynamicFieldSelector({
  insertMode,
  dynamicFields,
  templates,
  messageCursorPosition,
  setMessageCursorPosition,
  formik,
}) {
  const [selectedField, setSelectedField] = useState(null);
  const dispatch = useDispatch();
  const {
    currentMessage,
    isMessageUpdating,
  } = useSelector((state) => state?.chat || {});
  const { systemGenEditMessageReplaced } = currentMessage || {};

  const {
    values,
    setFieldValue,
    setFieldTouched,
  } = formik;

  const cpDynamicFields = useMemo(
    () => dynamicFields
      .filter((field) => (field?.screens?.includes('All') || field?.screens?.includes('Chat')) && field?.softwareName === 'Connection Point')
      .map((field) => ({
        label: field?.description,
        value: field?.value,
      })),
    [dynamicFields]
  );

  const templatesOptions = useMemo(() => {
    const list = templates?.map((template) => ({
      value: template.id,
      label: template.code,
      description: template.description,
    })) || [];

    return helpers.transform.arrayStringToObjectLabelValue(list);
  }, [templates]);

  const handleInsertDynamicField = () => {
    const currentText = values?.messageBody;
    const field = selectedField?.value;
    const isNullMessageCursorPosition = !messageCursorPosition && messageCursorPosition !== 0;
    const text = isNullMessageCursorPosition
      ? `${currentText}${field}`
      : `${currentText.substring(0, messageCursorPosition)}${field}${currentText.substring(messageCursorPosition)}`;
    const length = !isNullMessageCursorPosition ? messageCursorPosition + field.length : text.length;
    setFieldTouched('messageBody', true);
    setFieldValue('messageBody', text, true);
    setMessageCursorPosition(length);
  };

  const handleInsertTemplate = () => {
    const selectedTemplate = selectedField?.value;
    const messageTemplateDetails = helpers.transform.toArray(templates).find((template) => template?.id === selectedTemplate);
    const {
      systemGenMessage,
      attachment: messageTemplateDetailsAttachment,
      attachmentContainer: messageTemplateDetailsAttachmentContainer,
    } = messageTemplateDetails || {};

    if (systemGenMessage) {
      const dynamicFieldsObj = {
        calledBy: 'sendMessage',
        screenCalledFrom: 'Chat',
        distShort: false,
        contactId: values?.contactId || null,
      };
      dispatch(replaceSystemGenMessage(systemGenMessage, dynamicFieldsObj, isMessageEdit));
    }
    setFieldValue('messageBody', '', true);
    dispatch(setCurrentMessage({
      ...currentMessage,
      body: '',
      attachment: messageTemplateDetailsAttachment || null,
      messageAttachmentContainer: messageTemplateDetailsAttachmentContainer || null,
    }));
    dispatch(setFileUploadToMessage(false, isMessageEdit));
  };

  const insertModeConfig = {
    dynamicFields: {
      label: 'Select Dynamic Fields & Insert',
      inputLabel: 'Dynamic Fields',
      placeholder: 'Search or select a dynamic field',
      options: cpDynamicFields,
      filterOptions: filterOptionsDynamicFields,
      renderOption: renderOptionAutocomplete,
      buttonText: 'Insert',
      onClick: handleInsertDynamicField,
    },
    templates: {
      label: 'Enter & Select Template',
      inputLabel: 'Message Template & Scenario',
      placeholder: 'Search or select a template',
      options: templatesOptions,
      filterOptions: filterOptionsTemplates,
      renderOption: renderOptionAutocomplete,
      buttonText: 'Apply',
      onClick: handleInsertTemplate,
    },
  };

  const currentMode = insertModeConfig[insertMode];

  useEffect(() => {
    if (systemGenEditMessageReplaced) {
      setFieldValue('messageBody', systemGenEditMessageReplaced, true);
      dispatch(clearSystemGenMessageReplacedFlag(isMessageEdit));
    }
  }, [dispatch, systemGenEditMessageReplaced]);

  useEffect(() => {
    setSelectedField(null);
  }, [insertMode]);

  if (!currentMode) {
    return null;
  }

  return (
    <Grid
      container
      spacing={2}
      marginY="16px"
    >
      <Grid xs={12}>
        <Typography
          variant="h5"
        >
          {currentMode.label}
        </Typography>
      </Grid>
      <Grid
        xs={9}
        sm={10}
      >
        <Autocomplete
          options={currentMode.options}
          value={selectedField}
          filterOptions={currentMode.filterOptions}
          renderOption={currentMode.renderOption}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={(e, value) => setSelectedField(value)}
          renderInput={(params) => (
            <TextField
              label={currentMode.inputLabel}
              placeholder={currentMode.placeholder}
              {...params}
            />
          )}
          handleHomeEndKeys
          selectOnFocus
          clearOnBlur
        />
      </Grid>
      <Grid
        xs={3}
        sm={2}
      >
        <Button
          onClick={currentMode.onClick}
          disabled={!selectedField || isMessageUpdating}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            width: '100%',
            boxShadow: 'none !important',
            border: `1px solid ${STYLE_OPTIONS.COLOR_BLUE}`,
            height: 56,
          }}
        >
          {currentMode.buttonText}
        </Button>
      </Grid>
    </Grid>
  );
}

TemplateAndDynamicFieldSelector.propTypes = {
  insertMode: PropTypes.oneOf(['templates', 'dynamicFields']),
  dynamicFields: PropTypes.array,
  templates: PropTypes.array,
  messageCursorPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  setMessageCursorPosition: PropTypes.func,
  formik: PropTypes.object,
};
TemplateAndDynamicFieldSelector.defaultProps = {
  insertMode: '',
  dynamicFields: [],
  templates: [],
};

export default TemplateAndDynamicFieldSelector;
